/**
 * Constante del nombre del juego
 * NUNCA hardcodear el nombre del juego, siempre usar esta constante
 */
export const AUREUM_GAME = 'Aureum';

/**
 * URL base para assets (imágenes, sprites, etc.)
 * En desarrollo: '/assets' (servido por Vite)
 * En producción: URL del bucket (ej: 'https://cdn.example.com/assets')
 * 
 * Para configurar la URL del bucket en producción:
 * - Servidor (Node.js): Establecer variable de entorno ASSETS_CDN_URL
 * - Cliente (Browser): Configurar en vite.config.ts usando VITE_ASSETS_CDN_URL
 * 
 * Si no está definida, usa '/assets' (desarrollo local)
 */
let assetsBaseUrl = '/assets';

// Intentar obtener desde variable de entorno (Node.js)
if (typeof process !== 'undefined' && process.env?.ASSETS_CDN_URL) {
  assetsBaseUrl = process.env.ASSETS_CDN_URL;
}
// Intentar obtener desde Vite env (Browser). En Node (CJS) nunca se ejecuta
// porque el branch anterior ya capturó `process`, pero TS parsea el código
// igual y `import.meta` no está disponible en `module: commonjs` del server.
// Suprimimos con @ts-expect-error porque la intención es exactamente "ignorar
// este bloque en CJS"; si en el futuro el server pasa a ESM/Node16, el
// expect-error fallará y nos avisa que ya no es necesario.
// @ts-expect-error import.meta no existe en module: commonjs (build server)
else if (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_ASSETS_CDN_URL) {
  // @ts-expect-error import.meta no existe en module: commonjs (build server)
  assetsBaseUrl = (import.meta as any).env.VITE_ASSETS_CDN_URL;
}

export const ASSETS_BASE_URL = assetsBaseUrl;

// Base efectiva en runtime. Arranca con ASSETS_BASE_URL (env/Vite/default) pero se
// puede pisar al boot desde /config.json (setAssetsBaseUrl) — así, cuando los assets
// se muden a un bucket, alcanza con cambiar config.json + redeploy del static, sin
// rebuild. Las cargas que pasan por getAssetUrl respetan esta base.
let runtimeAssetsBase = ASSETS_BASE_URL;

/**
 * Pisa la base de assets en runtime (llamado al boot desde /config.json).
 * Quita el slash final para no duplicar.
 */
export function setAssetsBaseUrl(url: string): void {
  if (url && typeof url === 'string') {
    runtimeAssetsBase = url.endsWith('/') ? url.slice(0, -1) : url;
  }
}

/**
 * Constante para construir URLs de assets
 * Ejemplo: getAssetUrl('body/body1.png') -> '/assets/body/body1.png' o 'https://cdn.../body/body1.png'
 *
 * @param path Ruta relativa del asset (ej: 'item/sword_iron.png')
 * @returns URL completa del asset
 */
export function getAssetUrl(path: string): string {
  // Remover slash inicial si existe para evitar doble slash
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${runtimeAssetsBase}/${cleanPath}`;
}

/**
 * URL principal del sitio web
 * Usado para títulos, links y referencias del frontend
 */
export const SITE_URL = 'aureum.cc' as const;

/**
 * Links públicos del juego (usados en la pantalla de login, etc.)
 */
export const WEB_URL = 'https://aureum.cc' as const;
export const WIKI_URL = 'https://wiki.aureum.cc' as const;
export const DISCORD_URL = 'https://discord.gg/x4kXtAtyjf' as const;

/**
 * Configuración del servidor
 */
export const SERVER_CONFIG = {
  // Tickrate de simulación del servidor
  // 50Hz = 20ms por tick → el sistema de movimiento decide cuántos ticks por tile.
  TICK_RATE: 50,
  TICK_INTERVAL_MS: 20,
  PORT: 9000,
} as const;

/**
 * Configuración de movimiento estilo Argentum Online
 * Fuente única de verdad para cliente y servidor
 * 
 * El sistema de movimiento funciona así:
 * 1. Cliente envía intención de movimiento (dirección)
 * 2. Cliente inicia interpolación visual inmediatamente (client-side prediction)
 * 3. Servidor valida y confirma/rechaza el movimiento
 * 4. Cliente reconcilia si hay diferencia con el servidor
 * 
 * IMPORTANTE: STEP_DURATION_MS debe ser múltiplo de TICK_INTERVAL_MS (20ms)
 * para que servidor y cliente estén perfectamente sincronizados.
 */
export const MOVEMENT_CONFIG = {
  // Duración de movimiento de 1 tile en milisegundos
  // 200ms = 5 tiles por segundo (velocidad base estilo AO clásico)
  // Es exactamente 10 ticks del servidor (10 × 20ms = 200ms)
  STEP_DURATION_MS: 200,
  
  // Número de ticks del servidor por paso de movimiento
  // Derivado de STEP_DURATION_MS / TICK_INTERVAL_MS
  STEP_INTERVAL_TICKS: 10,
  
  // Máximo de pasos en cola del cliente
  // 2 pasos permite absorber latencia sin acumular demasiados inputs
  MAX_STEP_QUEUE: 2,
  
  // Umbral de distancia (en tiles) para decidir tipo de corrección:
  // - Menor a este valor: interpolación suave hacia posición del servidor
  // - Mayor o igual: snap/teleport instantáneo
  RECONCILE_THRESHOLD_TILES: 2,
  
  // Duración de la corrección suave cuando hay desync menor
  SMOOTH_CORRECT_MS: 100,
  
  // Velocidad en píxeles por milisegundo (calculado)
  // TILE_SIZE / STEP_DURATION_MS = 32 / 200 = 0.16 px/ms
  get PIXELS_PER_MS(): number {
    return TILE_SIZE / this.STEP_DURATION_MS;
  },
} as const;

/**
 * Duración base (ms) que una huella direccional (footstep sets 1–4) permanece en
 * el piso antes de desvanecerse. Es el valor base del stat `footstepDuration` de
 * las entidades — items / mods pueden aumentarlo o reducirlo. Los footsteps de
 * tipo animación (sets 5–7, ej. splash de agua) IGNORAN este stat: duran lo que
 * dura su animación. Ver docs/SPRITE_SYSTEM.md §Footsteps.
 */
export const FOOTSTEP_BASE_DURATION_MS = 2700;

/**
 * ID del mapa inicial / principal.
 * Todos los personajes nuevos y NPCs de ejemplo se crean en este mapa.
 */
export const DEFAULT_MAP_ID = 1 as const;

/**
 * Configuración base de combate. Constantes compartidas para que player y NPC
 * usen exactamente el mismo cooldown base de melee (dividido por attackSpeed
 * de la entidad atacante).
 */
export const COMBAT_CONFIG = {
  /** Cooldown base de un ataque melee (ms). Se divide por attackSpeed. */
  MELEE_BASE_COOLDOWN_MS: 1000,
  /** Stamina consumida por golpe melee sin arma (fallback si el arma no define `attackStaminaCost`). */
  MELEE_BASE_STAMINA_COST: 5,
  /** Radio de ataque base sin arma (unarmed). Ancho SIEMPRE impar. Ver docs/COMBAT_SYSTEMS.md. */
  MELEE_BASE_ATTACK_WIDTH: 1,
  MELEE_BASE_ATTACK_LENGTH: 1,
  MELEE_BASE_ATTACK_BACK: 0,
  /** Topes del Radio de ataque (largo "infinito" pero nunca > 5; atrás como mucho 1 tile). */
  MAX_ATTACK_LENGTH: 5,
  MAX_ATTACK_BACK: 1,
  /**
   * Duración (ms) de la protección de aparición post-login: 5s de inmunidad total
   * (no te targetean NPCs ni te pueden atacar/dañar). Se rompe con CUALQUIER acción
   * del jugador (mover, atacar, castear, meditar, levantar/usar ítems, interactuar
   * con NPC, seleccionar target, etc.). Ver docs/SPAWN_PROTECTION.md.
   */
  SPAWN_PROTECTION_MS: 5000,
  /**
   * Bloqueo físico. `blockChance` (0.0–1.0) da la probabilidad de bloquear un
   * golpe físico; se clampea a BLOCK_MAX_CHANCE al rollear. Un golpe bloqueado
   * mitiga su daño en BLOCK_DAMAGE_REDUCTION antes de aplicar la defensa física.
   */
  BLOCK_MAX_CHANCE: 0.75,
  BLOCK_DAMAGE_REDUCTION: 0.5,
  /** Cap de reducción del costo de maná (`manaReduction`, decimal). */
  MANA_REDUCTION_MAX: 0.75,
  /**
   * Intervalo (ms) de la regeneración periódica de maná del mod
   * `periodicManaRegenPercent` (keystone Gnomo "Trance Perpetuo"): cada tanto
   * restaura ese % del maná máximo, sustituyendo la meditación.
   */
  PERIODIC_MANA_REGEN_INTERVAL_MS: 5000,
} as const;

/**
 * Límite BASE de invocaciones simultáneas por jugador. Se sube con el mod aditivo
 * `summonLimit` (afijo, +1/+2/+3 por tier) o se FIJA con el único `summonLimitFixed`
 * (amuleto, 5). Lo consume SummonSystem.getSummonLimit. Ver docs/SUMMONS.md.
 */
export const SUMMON_BASE_LIMIT = 3;

/**
 * Configuración del sistema de pérdida de movilidad (Inmovilizar / Paralizar).
 * Durations en ms. Ver docs/CC_IMMOBILIZE_PARALYZE.md.
 */
export const IMPAIR = {
  /** Duración base del inmovilizar "común" (procs sin tier-duration propia, fallback). */
  COMMON_IMMOBILIZE_MS: 2500,
  /** Duración base del paralizar "común". Más corto que el inmovilizar (es más fuerte). */
  COMMON_PARALYZE_MS: 1500,
  /** Paralizar largo del status `freeze` (Congelar): variante de clase para entrenar NPCs. */
  FREEZE_PARALYZE_MS: 6000,
  /** Duración del inmovilizar del unique `immobilizeEveryHit` (proc en cada golpe). */
  UNIQUE_IMMOBILIZE_MS: 1200,
  /** Cap de chance de proc acumulada (decimal). */
  MAX_PROC_CHANCE: 1.0,
  /** Cap de reducción de duración por mods defensivos (decimal). */
  MAX_DURATION_REDUCTION: 0.8,
} as const;

/**
 * Invisibilidad (estado de sigilo). Ver docs/INVISIBILITY.md. Las duraciones por
 * hechizo/clase viven en el catálogo de spells; acá van los defaults del sistema.
 */
export const INVISIBILITY = {
  /** Duración por defecto de la invisibilidad mágica (fallback si un spell no la fija). */
  DEFAULT_DURATION_MS: 15000,
  /** Ventana de "revelado" tras una detección: el invisible se ve para TODOS. */
  REVEAL_MS: 4000,
  /** Radio (lado del cuadrado) del área de la poción de revelado: 9 = 9×9. */
  POTION_REVEAL_TILES: 9,
  /** Intervalo del pulso Predator (shimmer que "exhibe" el cuerpo): aleatorio en este rango. */
  SHIMMER_MIN_MS: 2000,
  SHIMMER_MAX_MS: 5000,
  /** Penalización de velocidad del sigilo permanente del asesino (decimal, -50%). */
  ASSASSIN_STEALTH_SLOW: 0.5,
  /** Cadencia del hechizo de detección de las invocaciones "del bosque" (cada 2 min). */
  FOREST_DETECT_INTERVAL_MS: 120000,
} as const;

// Tamaño de tile lógico para el cliente (en píxeles)
export const TILE_SIZE = 32;

/**
 * Resolución lógica del juego en tiles visibles.
 * Todos los clientes deben ver exactamente VIEW_TILES_X x VIEW_TILES_Y tiles,
 * independientemente del tamaño de la ventana. El resto se escala / letterbox.
 */
export const VIEW_TILES_X = 38 as const;
export const VIEW_TILES_Y = 25 as const;

/**
 * Área de Interés (AOI) / Interest Management.
 *
 * Half-range Chebyshev (en tiles) usado para:
 *  - @filterChildren del schema (qué entities sync el server a cada cliente)
 *  - target lock visibility (server valida en SELECT_TARGET / validatePlayerTargets)
 *  - broadcastToNearby (qué clients reciben los broadcasts de spell FX, chat, etc.)
 *
 * Se suma +1 al half de VIEW_TILES para evitar pop-in en el borde justo.
 */
export const VIEW_HALF_RANGE_X = Math.ceil(VIEW_TILES_X / 2) + 1; // 20
export const VIEW_HALF_RANGE_Y = Math.ceil(VIEW_TILES_Y / 2) + 1; // 14

/**
 * Cell size del spatial grid server-side (en tiles).
 * Elegido = max(VIEW_HALF_RANGE_*) para que un anillo 3×3 alrededor
 * de la cell del player cubra completamente su view.
 */
export const AOI_CELL_SIZE = 20 as const;

/**
 * Tamaño lógico del mundo en tiles (coordenadas de grilla)
 * El mapa va de (0,0) a (WORLD_TILES - 1, WORLD_TILES - 1)
 * 1225 = 35×35 celdas de 35 tiles (World Builder). Impar a propósito: así hay un
 * único centro del mapa (tile 612, con 612 tiles a cada lado) en vez de quedar la
 * mitad entre dos celdas. No requiere potencia de 2 (el spatial index/AOI usan
 * floor(tile / CELL) y toleran cualquier tamaño).
 */
export const WORLD_TILES = 1225 as const;

/**
 * Bounds derivados del tamaño del mundo
 */
export const WORLD_BOUNDS = {
  MIN_X: 0,
  MAX_X: WORLD_TILES - 1,
  MIN_Y: 0,
  MAX_Y: WORLD_TILES - 1,
} as const;

/**
 * Spawn por defecto: centro de la plaza de la Ciudad Principal (612,612),
 * siempre caminable y dentro del área segura. Lo usan la creación de personajes
 * (CharacterService.getSpawnPosition) y el rescate de join de GameRoom (si un
 * personaje carga sobre agua navegable sin barco, se lo reubica acá). Ver
 * docs/WATER_AND_BOATS.md.
 */
export const DEFAULT_SPAWN_POSITION = { x: 612, y: 612 } as const;

/**
 * Ancho del borde bloqueado del mapa (en tiles), por lado.
 * = ceil(DEFAULT_CELL_SIZE / 2) = la **mitad exterior** del anillo de celdas exterior
 * (con cellSize=35 → 18 tiles bloqueados, 17 jugables). Atado conceptualmente al
 * cellSize del World Builder; se mantiene como const separada para no crear import
 * circular constants↔worldBlueprint. Si cambia el cellSize, actualizar este valor.
 */
export const MAP_BORDER_SIZE = 18 as const;

/**
 * Tipos de entidades
 */
export enum EntityType {
  PLAYER = 'player',
  NPC = 'npc',
  MOB = 'mob',
}

/**
 * Niveles de rareza de items.
 *
 * - COMMON, RARE, EPIC: se rolean en runtime para templates con `affixable: true`.
 *   RARE suma 1 afijo aleatorio. EPIC suma 2.
 * - LEGENDARY: rareza fija de items con `unique: true` (no rolean afijos).
 * - SET: rareza fija de items con `setId` (no rolean afijos, otorgan bonos por piezas).
 */
export enum ItemRarity {
  COMMON = 0,
  RARE = 1,
  EPIC = 2,
  LEGENDARY = 3,
  SET = 4,
}

/**
 * Probabilidades de roll de rareza para templates `affixable`.
 * Suma debe dar 1.0.
 */
export const RARITY_ROLL_WEIGHTS: Record<ItemRarity.COMMON | ItemRarity.RARE | ItemRarity.EPIC, number> = {
  [ItemRarity.COMMON]: 0.9,
  [ItemRarity.RARE]: 0.09,
  [ItemRarity.EPIC]: 0.01,
};

/**
 * Cantidad de afijos rolled que otorga cada rareza.
 */
export const RARITY_AFFIX_COUNT: Record<ItemRarity, number> = {
  [ItemRarity.COMMON]: 0,
  [ItemRarity.RARE]: 1,
  [ItemRarity.EPIC]: 2,
  [ItemRarity.LEGENDARY]: 0, // afijos fijos del template
  [ItemRarity.SET]: 0,       // afijos fijos del template
};

/**
 * Pesos por defecto para rolear el tier de un afijo (T1, T2, T3).
 * T1 es el rango "peor", T3 el "mejor". Cada afijo puede sobreescribir esto
 * en su `AffixDefinition.tierWeights`. La suma debe dar 100 (proporcional).
 */
export const TIER_DEFAULT_WEIGHTS: readonly [number, number, number] = [70, 25, 5];

/**
 * Colores para cada rarity de items (en formato hexadecimal).
 * Usados para efectos visuales, nombres, bordes, tooltips.
 */
export const ITEM_RARITY_COLORS: Record<ItemRarity, number> = {
  [ItemRarity.COMMON]: 0xffffff,      // Blanco
  [ItemRarity.RARE]: 0x4a90ff,        // Azul
  [ItemRarity.EPIC]: 0xaa00ff,        // Violeta
  [ItemRarity.LEGENDARY]: 0xffaa00,   // Dorado/Naranja
  [ItemRarity.SET]: 0x00cc66,         // Verde esmeralda
};

/**
 * Nombres legibles de cada rareza (para tooltips, UI).
 */
export const ITEM_RARITY_NAMES: Record<ItemRarity, string> = {
  [ItemRarity.COMMON]: 'Común',
  [ItemRarity.RARE]: 'Raro',
  [ItemRarity.EPIC]: 'Épico',
  [ItemRarity.LEGENDARY]: 'Legendario',
  [ItemRarity.SET]: 'Set',
};

/**
 * Color específico para items de set (verde esmeralda).
 * Alias mantenido para compatibilidad con código existente.
 */
export const SET_ITEM_COLOR = ITEM_RARITY_COLORS[ItemRarity.SET];

/**
 * Economía — Exchange (mercado de fungibles). Fees son gold sinks: el oro
 * cobrado se QUEMA (sale del juego), no va a otro player. Ver
 * docs/ECONOMY_EXCHANGE_MARKETPLACE.md §6.
 */
export const MARKET_CONFIG = {
  /** Fee de transacción al vendedor, fracción del valor del fill (2%). */
  EXCHANGE_TX_FEE_RATE: 0.02,
  /** Fee fijo en oro al crear una orden (anti-spam de órdenes micro). */
  EXCHANGE_ORDER_FEE: 1,
  /** Tope de órdenes activas simultáneas por personaje en el Exchange. */
  EXCHANGE_MAX_ACTIVE_ORDERS: 20,
  /** Cuántos trades recientes se mandan al cliente para el historial del item. */
  EXCHANGE_RECENT_TRADES: 30,
  /** Distancia Chebyshev máxima al NPC Custodio para operar (igual que vendors). */
  EXCHANGE_MAX_DISTANCE: 2,
  /** Tope de listados activos del Marketplace por personaje. */
  MARKETPLACE_MAX_LISTINGS: 50,
  /** Cuántos listados devuelve una búsqueda por página. */
  MARKETPLACE_PAGE_SIZE: 40,
  /** Ventana (ms) de ventas comparables para la tasación (14 días). */
  APPRAISAL_WINDOW_MS: 14 * 24 * 60 * 60 * 1000,
  /** Descuento mínimo vs mediana para que un listado cuente como "oferta" (Deal Finder). */
  DEAL_MIN_DISCOUNT: 0.1,
  /** Tope de búsquedas guardadas + de vigilancias de precio por personaje. */
  TRADER_MAX_SAVED_SEARCHES: 20,
  TRADER_MAX_WATCHES: 20,
} as const;

/**
 * Color "dorado" canónico para textos relacionados con oro / precios /
 * valores monetarios. Usar consistentemente en MerchantPanel,
 * InventoryPanel, SpellVendorPanel, etc. Equivalente CSS / Phaser.
 */
export const GOLD_TEXT_COLOR = '#ffd700';
export const GOLD_TEXT_COLOR_HEX = 0xffd700;
/** Variante "no afford" — rojo claro, contrastante. */
export const PRICE_UNAFFORDABLE_COLOR = '#ff6b6b';

/**
 * Color canónico para labels / indicadores de NPCs BOSS (BOSS label en
 * TargetPanel, ★ BOSS ★ en NpcInfoPanel, notificación de respawn de boss
 * en GameScene). Dorado pálido distinto al `GOLD_TEXT_COLOR` para que
 * semánticamente no se confunda con moneda.
 */
export const BOSS_COLOR = '#ffd166';
export const BOSS_COLOR_HEX = 0xffd166;

/**
 * Slots de equipamiento
 */
export enum EquipmentSlot {
  HEAD = 'head',
  BODY = 'body',
  WEAPON = 'weapon',
  SHIELD = 'shield',
  RING = 'ring',
  BACK = 'back',
  BOOTS = 'boots',
  AMULET = 'amulet',
  /** Barco: permite navegar sobre agua. Equip/unequip gateado por agua/tierra enfrente. Ver docs/WATER_AND_BOATS.md. */
  BOAT = 'boat',
}

/**
 * Fuentes de Google Fonts disponibles para números de inventario (cantidad y símbolo de equipado)
 */
export const AVAILABLE_INVENTORY_FONTS = [
  { id: 'nunito-sans', name: 'Nunito Sans', family: '"Nunito Sans", sans-serif' },
  { id: 'montserrat', name: 'Montserrat', family: '"Montserrat", "Verdana", "Arial", sans-serif' },
] as const;

/**
 * Fuentes de Google Fonts disponibles para nombres de personajes (jugadores)
 */
export const AVAILABLE_PLAYER_NAMES_FONTS = [
  { id: 'nunito-sans', name: 'Nunito Sans', family: '"Nunito Sans", sans-serif' },
  { id: 'montserrat', name: 'Montserrat', family: '"Montserrat", "Verdana", "Arial", sans-serif' },
] as const;

/**
 * Fuente por defecto para todos los textos del juego (Phaser).
 * No afecta al frontend DOM (AuthUI/login), solo a los elementos del juego.
 *
 * Es Monogram: una pixel font CC0 (declarada como @font-face local en index.html,
 * archivo en client/assets/fonts/monogram/monogram.ttf). La usa TODO el texto
 * in-game "viejo": nombres de personajes/NPCs, globos de habla, palabras de magia,
 * números de daño, diálogos de NPC, HUD, menús, level-up, paneles y tooltips.
 *
 * Monogram tiene un solo peso: NO pedir `fontStyle: 'bold'` sobre ella (el faux-bold
 * del browser arruina la pixel font). El fallback monospace mantiene la métrica fija
 * si la TTF no cargó todavía.
 *
 * NO cubre la consola (SystemConsole usa su propia familia, tendrá fuente aparte).
 */
export const GAME_FONT_FAMILY = "'monogram', 'Courier New', monospace" as const;

/**
 * Fuente del nombre de entidades/personajes (labels sobre players/NPCs,
 * `GameScene.addPlayerNameText`): Pixeloid (pixel font, @font-face en index.html).
 * Separada de GAME_FONT_FAMILY a propósito: los nombres van en Pixeloid y el resto
 * del texto in-game (globos, daño, magias, HUD, paneles) en Monogram.
 */
export const ENTITY_NAME_FONT_FAMILY = "'pixeloid', 'Courier New', monospace" as const;

/**
 * Lista de todas las bitmap fonts disponibles en el juego.
 * Las bitmap fonts se cargan desde /assets/fonts/
 * Todas las fuentes comparten las mismas especificaciones:
 * - 19 caracteres por fila
 * - 30x30 píxeles por celda
 * - Las letras están centradas en su cuadrado
 * - El tamaño de fuente puede variar por tipografía
 */
export const AVAILABLE_BITMAP_FONTS = [
  { id: 'born2bsporty', name: 'Born 2B Sporty', fontSize: 12 },
  { id: 'brokenconsole', name: 'Broken Console', fontSize: 12 },
] as const;

/**
 * Fuentes por defecto (se pueden cambiar en opciones).
 */
export const DEFAULT_BITMAP_FONTS = {
  UI: 'born2bsporty', // Fuente por defecto para paneles, chat, inventario, etc.
} as const;

/**
 * Configuración de las bitmap fonts.
 * Todas las fuentes comparten las mismas especificaciones:
 * - 19 caracteres por fila
 * - 30x30 píxeles por celda
 * - Las letras están centradas en su cuadrado
 * - El tamaño de fuente puede variar por tipografía
 */
export const BITMAP_FONT_CONFIG = {
  CHARS_PER_ROW: 19,
  GRID_WIDTH: 30,
  GRID_HEIGHT: 30,
  CHARACTERS: ' !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~',
} as const;

/**
 * Bitmap font REAL de Monogram (atlas PNG horneado a 16px + `.fnt` per-char generado
 * desde `monogram.ttf` con `client/scripts/gen_monogram_bmf.py`). A diferencia de
 * `AVAILABLE_BITMAP_FONTS` (grilla monoespaciada ASCII sin acentos), este cubre todo
 * el charset del juego (áéíóúñ¡¿…) con los avances reales de la fuente.
 *
 * Por qué existe: `add.text()` con la TTF rasteriza el glifo con antialiasing en CADA
 * tamaño, y a tamaños fuera de la grilla nativa de Monogram (múltiplos de 16px) los
 * bordes caen en fracciones de pixel distintas por glifo → "la misma letra se ve
 * distinta". El BitmapText copia los pixeles del atlas 1:1 (cero rasterizado por
 * tamaño) → consistencia pixel-perfect.
 *
 * CLAVE: el atlas está horneado a 16px, así que solo es crisp a `fontSize` **múltiplo
 * de 16** (16 = ×1 nativo, 32 = ×2, 48 = ×3). Cualquier otro tamaño reescala el atlas
 * por un factor no entero y vuelve el problema. Usar `snapMonogramBmpSize()`.
 */
export const MONOGRAM_BMP_FONT = 'monogram_bmp' as const;

/** Tamaño nativo (crisp) del atlas de {@link MONOGRAM_BMP_FONT}, en px. */
export const MONOGRAM_BMP_BASE_SIZE = 16 as const;

/**
 * Ajusta un tamaño deseado (px) al múltiplo de {@link MONOGRAM_BMP_BASE_SIZE} más
 * cercano (mínimo uno), que es el único tamaño al que el BitmapText de Monogram cae
 * clavado en la grilla de pixeles. Sin esto, un tamaño intermedio reescala el atlas
 * por un factor no entero y reintroduce la fuzz.
 */
export function snapMonogramBmpSize(px: number): number {
  const base = MONOGRAM_BMP_BASE_SIZE;
  const mult = Math.max(1, Math.round(px / base));
  return mult * base;
}

/**
 * Genera el XML para una bitmap font basado en las especificaciones estándar.
 * @param fontId ID de la fuente
 * @param fontSize Tamaño de la fuente (debe coincidir con el tamaño visual)
 * @returns String XML para la fuente
 */
export function generateBitmapFontXML(fontId: string, fontSize: number): string {
  const { CHARS_PER_ROW, GRID_WIDTH, GRID_HEIGHT, CHARACTERS } = BITMAP_FONT_CONFIG;
  
  // El xadvance debe ser menor que el fontSize para que las letras estén juntas
  // Usamos aproximadamente 70% del fontSize para un espaciado más compacto
  const xadvance = Math.max(6, Math.floor(fontSize * 0.7));
  
  // Calcular el número de filas necesarias
  const totalChars = CHARACTERS.length;
  const rows = Math.ceil(totalChars / CHARS_PER_ROW);
  
  // Calcular el tamaño total de la imagen
  const imageWidth = CHARS_PER_ROW * GRID_WIDTH;
  const imageHeight = rows * GRID_HEIGHT;
  
  // Generar los caracteres
  let charsXML = '';
  for (let i = 0; i < totalChars; i++) {
    const char = CHARACTERS[i];
    const charCode = char.charCodeAt(0);
    const row = Math.floor(i / CHARS_PER_ROW);
    const col = i % CHARS_PER_ROW;
    const x = col * GRID_WIDTH;
    const y = row * GRID_HEIGHT;
    
    // Las letras están centradas en su celda de 30x30
    // El xoffset debe ser 0 para que se dibujen desde el punto de origen sin desplazamiento
    // El yoffset también debe ser 0 para mantener la alineación vertical
    charsXML += `    <char id="${charCode}" x="${x}" y="${y}" width="${GRID_WIDTH}" height="${GRID_HEIGHT}" xoffset="0" yoffset="0" xadvance="${xadvance}" page="0" chnl="15"/>\n`;
  }
  
  return `<?xml version="1.0"?>
<font>
  <info face="${fontId}" size="${fontSize}" bold="0" italic="0" charset="" unicode="1" stretchH="100" smooth="0" aa="1" padding="0,0,0,0" spacing="0,0" outline="0"/>
  <common lineHeight="${GRID_HEIGHT}" base="${fontSize}" scaleW="${imageWidth}" scaleH="${imageHeight}" pages="1" packed="0" alphaChnl="0" redChnl="4" greenChnl="4" blueChnl="4"/>
  <pages>
    <page id="0" file="${fontId}.png"/>
  </pages>
  <chars count="${totalChars}">
${charsXML}  </chars>
  <kernings count="0"/>
</font>`;
}

